import { useState, useEffect, useCallback } from 'react';
import { holidaysAPI } from '../../../services/api';

interface Holiday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  description?: string;
  isActive: boolean;
}

interface HolidayFormData {
  name: string;
  startDate: string;
  endDate: string;
  description?: string;
}

export const useHolidays = (isAdmin: boolean, year: number) => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await holidaysAPI.getAll(year);
      setHolidays(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  }, [year]);

  const createHoliday = async (data: HolidayFormData) => {
    return holidaysAPI.create(data);
  };

  const updateHoliday = async (id: string, data: HolidayFormData) => {
    return holidaysAPI.update(id, data);
  };

  const deleteHoliday = async (id: string) => {
    await holidaysAPI.delete(id);
  };

  const handleCreateHoliday = async (data: HolidayFormData) => {
    try {
      await createHoliday(data);
      setSuccess('Holiday created successfully!');
      await fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create holiday');
      throw err;
    }
  };

  const handleUpdateHoliday = async (id: string, data: HolidayFormData) => {
    try {
      await updateHoliday(id, data);
      setSuccess('Holiday updated successfully!');
      await fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update holiday');
      throw err;
    }
  };

  const handleDeleteHoliday = async () => {
    if (!selectedHoliday) return;

    try {
      setIsDeleting(true);
      await deleteHoliday(selectedHoliday.id);
      setSuccess('Holiday deleted successfully!');
      await fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
      setDeleteDialogOpen(false);
      setSelectedHoliday(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete holiday');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (holiday: Holiday) => {
    setSelectedHoliday(holiday);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedHoliday(null);
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    if (isAdmin) {
      fetchHolidays();
    }
  }, [isAdmin, fetchHolidays]);

  return {
    holidays,
    loading,
    error,
    success,
    handleCreateHoliday,
    handleUpdateHoliday,
    handleDeleteHoliday,
    openDeleteDialog,
    closeDeleteDialog,
    deleteDialogOpen,
    selectedHoliday,
    isDeleting,
    clearMessages,
    refetch: fetchHolidays,
  };
};
